import { create } from 'zustand'
import { casesApi } from '../services'

const normalizeCasesData = (casesData) => {
  if (Array.isArray(casesData)) {
    return casesData
  }

  if (casesData && typeof casesData === 'object') {
    return Object.values(casesData)
  }

  return []
}

const normalizeCategoryOrder = (categoryOrder) => {
  if (!Array.isArray(categoryOrder)) {
    return []
  }

  return categoryOrder.filter(
    (category) => typeof category === 'string' && category.trim() !== ''
  )
}

const useCasesStore = create((set, get) => ({
  // Cases data
  cases: [],
  categoryOrder: [],
  currentCase: null,
  
  // Case opening state
  isOpening: false,
  openingResult: null,
  isLoading: false,
  error: null,
  
  // Opening data from API
  openingData: null,
  
  // Multiple opening state - состояние множественного открытия
  openingMultipleResults: null, // Массив результатов множественного открытия
  isOpeningMultiple: false, // Флаг процесса множественного открытия
  
  // Actions
  setCases: (cases) => set({ cases: normalizeCasesData(cases), error: null }),
  setCategoryOrder: (categoryOrder) => set({ categoryOrder: normalizeCategoryOrder(categoryOrder) }),
  setCurrentCase: (caseData) => set({ currentCase: caseData }),
  setOpeningResult: (result) => set({ openingResult: result }),
  setOpeningData: (data) => set({ openingData: data }),
  setLoading: (isLoading) => set({ isLoading }),
  setError: (error) => set({ error, isLoading: false }),
  
  // Get case by ID from the cases list
  getCaseById: (caseId) => {
    const { cases } = get()
    return cases.find(c => c.id === caseId)
  },
  
  // Load cases from API
  loadCases: async () => {
    try {
      set({ isLoading: true, error: null })
      const response = await casesApi.getCases()
      const normalizedCases = normalizeCasesData(response.cases)
      const normalizedCategoryOrder = normalizeCategoryOrder(response.category_order)
      
      // Check if we have cases data
      if (normalizedCases.length === 0) {
        throw new Error('No cases available')
      }
      
      set({ cases: normalizedCases, categoryOrder: normalizedCategoryOrder })
      
      // Response is processed by the universal API handler
    } catch (error) {
      console.error('Failed to load cases:', error)
      set({ error: error.message || 'Failed to load cases' })
    } finally {
      set({ isLoading: false })
    }
  },
  
  // Load case details
  loadCaseDetails: async (caseId) => {
    try {
      set({ isLoading: true, error: null, currentCase: null })
      const response = await casesApi.getCaseDetails(caseId)
      
      // Check if we have case data
      if (!response.case) {
        throw new Error('Case not found')
      }
      
      // Set the full case data from API response
      set({ currentCase: response.case })
      
      // Response is processed by the universal API handler
    } catch (error) {
      console.error('Failed to load case details:', error)
      set({ error: error.message || 'Failed to load case details' })
    } finally {
      set({ isLoading: false })
    }
  },
  
  // Open a case with real API call
  openCase: async (caseId) => {
    try {
      set({ isOpening: true, openingResult: null, openingData: null, error: null })
      
      // Call API to open case
      const response = await casesApi.openCase(caseId)
      
      console.log('🎰 Full API response:', response)
      console.log('🎯 Opening data:', response.open)
      
      if (!response.open) {
        throw new Error('Invalid opening response')
      }
      
      // Store opening data for animation AND result immediately
      set({ 
        openingData: response.open,
        openingResult: response.open // Устанавливаем результат сразу
      })
      
      console.log('🎁 Result set immediately:', response.open)
      
      // Add 11-second delay before clearing isOpening flag
      // Это позволяет анимации завершиться, но результат уже доступен
      await new Promise(resolve => setTimeout(resolve, 11000))
      
      // Теперь безопасно сбрасываем isOpening
      set({ isOpening: false })
      
      return response.open
    } catch (error) {
      console.error('Failed to open case:', error)
      set({ error: error.message || 'Failed to open case' })
      set({ isOpening: false })
      throw error
    }
  },
  
  clearOpeningResult: () => set({ openingResult: null, openingData: null }),
  clearError: () => set({ error: null }),
  
  /**
   * Открыть кейс несколько раз (множественное открытие)
   * @param {string} caseId - ID кейса
   * @param {number} count - Количество открытий (2-10)
   */
  openCaseMultiple: async (caseId, count) => {
    try {
      set({ 
        isOpeningMultiple: true, 
        openingMultipleResults: null, 
        error: null 
      })
      
      // Вызываем API для множественного открытия
      const response = await casesApi.openCaseMultiple(caseId, count)
      
      console.log('🎰 Multiple opening API response:', response)
      console.log('🎯 Multiple opening data:', response.open)
      
      // Проверяем наличие ошибки в ответе
      if (response.error) {
        throw new Error(response.error)
      }
      
      if (!response.open || !Array.isArray(response.open)) {
        throw new Error('Неверный формат ответа от сервера')
      }
      
      // Сохраняем массив результатов
      set({ 
        openingMultipleResults: response.open,
        isOpeningMultiple: false
      })
      
      console.log('🎁 Multiple results set:', response.open)
      
      return response.open
    } catch (error) {
      console.error('Failed to open case multiple times:', error)
      const errorMessage = error.response?.data?.error || error.message || 'Не удалось открыть кейсы'
      set({ 
        error: errorMessage,
        isOpeningMultiple: false 
      })
      throw error
    }
  },
  
  // Очистить результаты множественного открытия
  clearMultipleResults: () => set({ openingMultipleResults: null })
}))

export default useCasesStore
